// ==========================================================================
// CONSTANTES OFICIAIS DE STATUS — PLATAFORMA 30GO
// ==========================================================================
// Este arquivo é a FONTE ÚNICA DE VERDADE para todos os status da plataforma.
// Todas as comparações de status no frontend devem usar estas constantes
// e funções de normalização, em vez de strings literais espalhadas.
// ==========================================================================

// ─── ITENS ────────────────────────────────────────────────────────────────

export const ITEM_STATUS = {
  PENDENTE: 'pendente',
  APROVADO: 'aprovado',
  EM_DISPUTA: 'em_disputa',
  VENDIDO: 'vendido',
  SEM_LANCE: 'sem_lance',
  CANCELADO: 'cancelado',
  REPROVADO: 'reprovado',
  AGUARDANDO_PAGAMENTO: 'aguardando_pagamento',
  PAGO: 'pago',
  AGUARDANDO_RETIRADA: 'aguardando_retirada',
  ENTREGUE: 'entregue',
  EXPIRADO: 'expirado',
} as const;

export type ItemStatus = typeof ITEM_STATUS[keyof typeof ITEM_STATUS];

/** Status de itens que são considerados finalizados (não participam mais de disputas) */
export const ITEM_STATUS_FINALIZADOS: readonly string[] = [
  ITEM_STATUS.VENDIDO,
  ITEM_STATUS.SEM_LANCE,
  ITEM_STATUS.CANCELADO,
  ITEM_STATUS.REPROVADO,
];

/** Status de itens que são visíveis em pré-disputa ou ao vivo */
export const ITEM_STATUS_VISIVEIS: readonly string[] = [
  ITEM_STATUS.APROVADO,
  ITEM_STATUS.EM_DISPUTA,
];

/** Status de itens que estão em disputa ativa */
export const ITEM_STATUS_DISPUTA: readonly string[] = [
  ITEM_STATUS.APROVADO,
  ITEM_STATUS.EM_DISPUTA,
];

/** Status de itens que podem ser deletados pelo vendedor */
export const ITEM_STATUS_DELETAVEIS: readonly string[] = [
  ITEM_STATUS.PENDENTE,
  ITEM_STATUS.APROVADO,
];

/** Status de itens que bloqueiam exclusão */
export const ITEM_STATUS_BLOQUEADOS_DELECAO: readonly string[] = [
  ITEM_STATUS.EM_DISPUTA,
  ITEM_STATUS.VENDIDO,
];

// ─── EVENTOS ──────────────────────────────────────────────────────────────

export const EVENTO_STATUS = {
  EM_PREPARACAO: 'em_preparacao',
  PRE_DISPUTA: 'pre_disputa',
  AO_VIVO: 'ao_vivo',
  AGUARDANDO_PAGAMENTO: 'aguardando_pagamento',
  FINALIZADO: 'finalizado',
  CANCELADO: 'cancelado',
} as const;

export type EventoStatus = typeof EVENTO_STATUS[keyof typeof EVENTO_STATUS];

/** Status de eventos ativos (que ainda não foram encerrados) */
export const EVENTO_STATUS_ATIVOS: readonly string[] = [
  EVENTO_STATUS.EM_PREPARACAO,
  EVENTO_STATUS.PRE_DISPUTA,
  EVENTO_STATUS.AO_VIVO,
  EVENTO_STATUS.AGUARDANDO_PAGAMENTO,
];

/** Status de eventos que permitem cadastro de itens */
export const EVENTO_STATUS_CADASTRO_ABERTO: readonly string[] = [
  EVENTO_STATUS.EM_PREPARACAO,
  EVENTO_STATUS.PRE_DISPUTA,
];

// ─── PAGAMENTOS ───────────────────────────────────────────────────────────

export const PAGAMENTO_STATUS = {
  PENDING: 'pending',
  PAGO: 'pago',
  EXPIRADO: 'expirado',
  CANCELLED: 'cancelled',
  ENTREGUE: 'entregue',
} as const;

export type PagamentoStatus = typeof PAGAMENTO_STATUS[keyof typeof PAGAMENTO_STATUS];

// ─── USUARIOS ─────────────────────────────────────────────────────────────

export const USUARIO_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',
  PENDENTE_ANALISE: 'pendente_analise',
  SUSPENDED: 'suspended',
  REPROVADO: 'reprovado',
} as const;

export type UsuarioStatus = typeof USUARIO_STATUS[keyof typeof USUARIO_STATUS];

/** Status de usuário que são considerados ativos/autorizados */
export const USUARIO_STATUS_ATIVOS: readonly string[] = [
  USUARIO_STATUS.ACTIVE,
];

// ─── EMPRESAS ─────────────────────────────────────────────────────────────

export const EMPRESA_STATUS = {
  PENDENTE: 'pendente',
  VERIFICADO: 'verificado',
  BLOQUEADO: 'bloqueado',
} as const;

export type EmpresaStatus = typeof EMPRESA_STATUS[keyof typeof EMPRESA_STATUS];

// ─── CADASTROS EMPRESA WHATSAPP ───────────────────────────────────────────

export const CADASTRO_WA_STATUS = {
  PENDENTE: 'pendente',
  APROVADO: 'aprovado',
  RECUSADO: 'recusado',
} as const;

export type CadastroWAStatus = typeof CADASTRO_WA_STATUS[keyof typeof CADASTRO_WA_STATUS];

// ==========================================================================
// ALIASES — MAPEAMENTO DE STATUS LEGADOS → STATUS OFICIAIS
// ==========================================================================

// ─── ITENS aliases ────────────────────────────────────────────────────────

const ITEM_ALIAS_MAP: Record<string, string> = {
  pending: ITEM_STATUS.PENDENTE,
  approved: ITEM_STATUS.APROVADO,
  active: ITEM_STATUS.EM_DISPUTA,
  sold: ITEM_STATUS.VENDIDO,
  cancelled: ITEM_STATUS.CANCELADO,
  rejected: ITEM_STATUS.REPROVADO,
  completed: ITEM_STATUS.VENDIDO, // legado
  retirado: ITEM_STATUS.ENTREGUE,
  pre_dispute: ITEM_STATUS.APROVADO, // legado SQL
  pre_disputa: ITEM_STATUS.APROVADO, // status de evento erroneamente em item
  ao_vivo: ITEM_STATUS.EM_DISPUTA, // status de evento erroneamente em item
};

// ─── EVENTOS aliases ─────────────────────────────────────────────────────

const EVENTO_ALIAS_MAP: Record<string, string> = {
  active: EVENTO_STATUS.AO_VIVO,
  preparacao: EVENTO_STATUS.EM_PREPARACAO,
  completed: EVENTO_STATUS.FINALIZADO,
  encerrado: EVENTO_STATUS.FINALIZADO,
  cancelled: EVENTO_STATUS.CANCELADO,
};

// ─── PAGAMENTOS aliases ──────────────────────────────────────────────────

const PAGAMENTO_ALIAS_MAP: Record<string, string> = {
  paid: PAGAMENTO_STATUS.PAGO,
  confirmed: PAGAMENTO_STATUS.PAGO,
  approved: PAGAMENTO_STATUS.PAGO,
  completed: PAGAMENTO_STATUS.PAGO,
  expired: PAGAMENTO_STATUS.EXPIRADO,
  cancelado: PAGAMENTO_STATUS.CANCELLED,
  aguardando_pagamento: PAGAMENTO_STATUS.PENDING,
  pendente: PAGAMENTO_STATUS.PENDING,
  nao_pago: PAGAMENTO_STATUS.CANCELLED,
  rejected: PAGAMENTO_STATUS.CANCELLED,
  processing: PAGAMENTO_STATUS.PENDING,
  failed: PAGAMENTO_STATUS.CANCELLED,
};

// ─── USUARIOS aliases ────────────────────────────────────────────────────

const USUARIO_ALIAS_MAP: Record<string, string> = {
  ativo: USUARIO_STATUS.ACTIVE,
  aprovado: USUARIO_STATUS.ACTIVE,
  approved: USUARIO_STATUS.ACTIVE,
  pendente: USUARIO_STATUS.PENDING,
  em_analise: USUARIO_STATUS.PENDENTE_ANALISE,
  suspenso: USUARIO_STATUS.SUSPENDED,
  bloqueado: USUARIO_STATUS.SUSPENDED,
};

// ==========================================================================
// FUNÇÕES DE NORMALIZAÇÃO
// ==========================================================================

/**
 * Normaliza um status de item para o valor oficial.
 * Aceita aliases legados (sold, approved, active, etc) e retorna o status oficial.
 * Se o status já for oficial, retorna ele mesmo.
 * Se não for reconhecido, retorna o valor original (seguro para novos status).
 */
export function normalizarStatusItem(status: string | null | undefined): string {
  if (!status) return ITEM_STATUS.PENDENTE;
  const s = status.toLowerCase().trim();
  // Se já é um status oficial, retorna direto
  const oficialValues = Object.values(ITEM_STATUS) as string[];
  if (oficialValues.includes(s)) return s;
  // Tenta mapear de alias
  if (ITEM_ALIAS_MAP[s]) return ITEM_ALIAS_MAP[s];
  // Fallback: retorna o valor original
  return status;
}

/**
 * Normaliza um status de evento para o valor oficial.
 */
export function normalizarStatusEvento(status: string | null | undefined): string {
  if (!status) return EVENTO_STATUS.FINALIZADO;
  const s = status.toLowerCase().trim();
  const oficialValues = Object.values(EVENTO_STATUS) as string[];
  if (oficialValues.includes(s)) return s;
  if (EVENTO_ALIAS_MAP[s]) return EVENTO_ALIAS_MAP[s];
  return status;
}

/**
 * Normaliza um status de pagamento para o valor oficial.
 */
export function normalizarStatusPagamento(status: string | null | undefined): string {
  if (!status) return PAGAMENTO_STATUS.PENDING;
  const s = status.toLowerCase().trim();
  const oficialValues = Object.values(PAGAMENTO_STATUS) as string[];
  if (oficialValues.includes(s)) return s;
  if (PAGAMENTO_ALIAS_MAP[s]) return PAGAMENTO_ALIAS_MAP[s];
  return status;
}

/**
 * Normaliza um status de usuário para o valor oficial.
 */
export function normalizarStatusUsuario(status: string | null | undefined): string {
  if (!status) return USUARIO_STATUS.PENDING;
  const s = status.toLowerCase().trim();
  const oficialValues = Object.values(USUARIO_STATUS) as string[];
  if (oficialValues.includes(s)) return s;
  if (USUARIO_ALIAS_MAP[s]) return USUARIO_ALIAS_MAP[s];
  return status;
}

// ==========================================================================
// FUNÇÕES AUXILIARES DE COMPARAÇÃO
// ==========================================================================

/**
 * Verifica se um status de item (incluindo aliases) é equivalente a um status oficial.
 * Ex: statusEqItem('sold', ITEM_STATUS.VENDIDO) → true
 */
export function statusEqItem(status: string | null | undefined, oficial: string): boolean {
  return normalizarStatusItem(status) === oficial;
}

/**
 * Verifica se um status de item pertence a uma lista de status oficiais (aceita aliases).
 */
export function statusItemInList(status: string | null | undefined, lista: readonly string[]): boolean {
  const normalized = normalizarStatusItem(status);
  return lista.includes(normalized);
}

/**
 * Verifica se um status de evento (incluindo aliases) é equivalente a um status oficial.
 */
export function statusEqEvento(status: string | null | undefined, oficial: string): boolean {
  return normalizarStatusEvento(status) === oficial;
}

/**
 * Verifica se um status de evento pertence a uma lista de status oficiais (aceita aliases).
 */
export function statusEventoInList(status: string | null | undefined, lista: readonly string[]): boolean {
  const normalized = normalizarStatusEvento(status);
  return lista.includes(normalized);
}

/**
 * Verifica se um status de pagamento (incluindo aliases) é equivalente a um status oficial.
 */
export function statusEqPagamento(status: string | null | undefined, oficial: string): boolean {
  return normalizarStatusPagamento(status) === oficial;
}

/**
 * Verifica se um pagamento está concluído com sucesso.
 * Considera tanto 'pago' quanto 'entregue' como estados de pagamento concluído.
 * "Entregue" significa: pagamento foi confirmado E a entrega já foi realizada.
 * Em ambos os casos, o ciclo de pagamento está completo.
 */
export function isPagamentoConcluido(status: string | null | undefined): boolean {
  const normalized = normalizarStatusPagamento(status);
  return normalized === PAGAMENTO_STATUS.PAGO || normalized === PAGAMENTO_STATUS.ENTREGUE;
}

/**
 * Verifica se um status de usuário (incluindo aliases) é equivalente a um status oficial.
 */
export function statusEqUsuario(status: string | null | undefined, oficial: string): boolean {
  return normalizarStatusUsuario(status) === oficial;
}